/**
 * SchoolSetupChecklist — Shows admins exactly what they need to configure
 * before Teacher / Student / Parent portals will display meaningful data.
 *
 * Calls GET /api/school/tenants/{tenantId}/setup-status and renders a
 * collapsible card with a progress bar + per-item status + quick-link to
 * jump to the right admin section.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle2, Circle, AlertTriangle, ChevronDown, ChevronUp,
  ListChecks, ArrowRight, Sparkles, Loader2,
} from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fetchStatus = async (tenantId) => {
  const res = await fetch(`${API_URL}/api/school/tenants/${tenantId}/setup-status`, {
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
  });
  if (!res.ok) throw new Error('Failed to load setup status');
  return res.json();
};

export default function SchoolSetupChecklist({ tenantId, onNavigate }) {
  const { data, isLoading } = useQuery({
    queryKey: ['school-setup-status', tenantId],
    queryFn: () => fetchStatus(tenantId),
    enabled: !!tenantId,
    refetchOnWindowFocus: false,
  });

  // Auto-expanded by default until ready, then collapsed
  const [expanded, setExpanded] = useState(true);
  React.useEffect(() => {
    if (data && data.ready_for_portals) setExpanded(false);
  }, [data]);

  if (isLoading) {
    return (
      <Card className="border-amber-200 bg-amber-50/50" data-testid="setup-checklist-card">
        <CardContent className="py-6 flex items-center gap-2 text-sm text-amber-700">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking setup status…
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const ready = data.ready_for_portals;
  const percent = data.percent || 0;
  const tone = ready ? 'emerald' : percent >= 60 ? 'amber' : 'rose';
  const toneClasses = {
    emerald: { card: 'border-emerald-200 bg-emerald-50/50', bar: 'bg-emerald-500', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    amber: { card: 'border-amber-200 bg-amber-50/50', bar: 'bg-amber-500', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700 border-amber-200' },
    rose: { card: 'border-rose-200 bg-rose-50/50', bar: 'bg-rose-500', text: 'text-rose-700', badge: 'bg-rose-100 text-rose-700 border-rose-200' },
  }[tone];

  return (
    <Card className={`${toneClasses.card} border-2`} data-testid="setup-checklist-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {ready ? (
              <Sparkles className={`h-5 w-5 ${toneClasses.text} shrink-0`} />
            ) : (
              <ListChecks className={`h-5 w-5 ${toneClasses.text} shrink-0`} />
            )}
            <div className="min-w-0">
              <CardTitle className="text-base font-semibold text-slate-900 truncate">
                {ready ? 'Your school is ready for teachers & students' : 'Finish setting up your school'}
              </CardTitle>
              <p className={`text-xs ${toneClasses.text} mt-0.5`}>
                {ready
                  ? 'All required steps complete. Portals will show meaningful data.'
                  : `${data.completed}/${data.total_required} required steps complete — finish these so teachers and students see classes, timetables and announcements.`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Badge variant="outline" className={toneClasses.badge} data-testid="setup-percent-badge">
              {percent}%
            </Badge>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setExpanded(!expanded)}
              data-testid="setup-toggle-btn"
              aria-label={expanded ? 'Collapse checklist' : 'Expand checklist'}
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        {/* Progress bar */}
        <div className="w-full h-2 bg-white/70 rounded-full overflow-hidden mt-2">
          <div
            className={`h-full ${toneClasses.bar} transition-all duration-500 ease-out`}
            style={{ width: `${percent}%` }}
          />
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-2">
          <ul className="space-y-1.5">
            {(data.items || []).map((item) => (
              <li
                key={item.key}
                className="flex items-start gap-2.5 py-1.5 px-2 rounded-md hover:bg-white/60 transition-colors"
                data-testid={`setup-item-${item.key}`}
              >
                <span className="mt-0.5 shrink-0">
                  {item.complete ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  ) : item.required ? (
                    <Circle className="h-4 w-4 text-rose-400" />
                  ) : (
                    <Circle className="h-4 w-4 text-slate-300" />
                  )}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm font-medium ${item.complete ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
                      {item.label}
                    </span>
                    {!item.required && (
                      <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-slate-300 text-slate-500">Optional</Badge>
                    )}
                    {typeof item.count === 'number' && item.count > 0 && (
                      <span className="text-[11px] text-slate-500">· {item.count}</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{item.description}</p>
                  {item.warning && (
                    <div className="flex items-center gap-1 mt-1 text-[11px] text-amber-700">
                      <AlertTriangle className="h-3 w-3" />
                      <span>{item.warning}</span>
                    </div>
                  )}
                </div>
                {!item.complete && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs h-7 shrink-0"
                    onClick={() => onNavigate && onNavigate(item.action_path)}
                    data-testid={`setup-go-${item.key}`}
                  >
                    Set up <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </CardContent>
      )}
    </Card>
  );
}
